import React, { useEffect, useState } from "react";
import { prepFqReportStyles } from "../styles/prepFqReportStyles";
import { useTranslation } from "next-i18next";
import { containerStyles } from "../../../theme/containerStyles";
import clsx from "clsx";
import Container from "@mui/material/Container";
import PrioritiesBanner from "./PrioritiesBanner";
import SuggestionsSection from "./SuggestionsSection";
import { Waypoint } from "react-waypoint";
import QuestionIcon from "../icons/QuestionIcon";
import { useAuth } from "../../../firebase/useAuth";
import useUser from "../../../firebase/useUser";
import { Collapse, Fade, Grid, IconButton } from "@mui/material";
import usePrepFqProfile from "../fetchers/usePrepFqProfile";
import usePurchasedEnhance from "../fetchers/usePurchasedEnhance";
import CircularProgress from "@mui/material/CircularProgress";
import TopicModal from "./TopicModal";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { useLearningModules } from "../fetchers/learning_modules/useLearningModules";
import useAveragePrepFqScores from "../fetchers/useAveragePrepFqScores";
import JournalEntriesCard from "./JournalEntriesCard";
import NotYetApplicableCard from "./NotYetApplicableCard";
import BcgModal from "./TopicModal/BcgModal";

const Tour = dynamic(
  () => {
    return import("./PrepFqTour");
  },
  { ssr: false }
);

const PrepFqReportRoot = () => {
  const classes = prepFqReportStyles();
  const containerClasses = containerStyles();
  const { t } = useTranslation(["prepFq"]);
  const router = useRouter();
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const authUser = useAuth();
  const { user, isLoading: userLoading } = useUser(authUser?.user?.uid);
  const { profile, isLoading: profileLoading } = usePrepFqProfile(
    user?.user_id
  );
  const { purchasedEnhance, isLoading: purchasesLoading }: any =
    usePurchasedEnhance(user?.user_id);
  const { isLoading: averagesLoading } = useAveragePrepFqScores(
    profile?.prep_fq_profile_id,
    user,
    profile
  );
  const [cardHeight, setCardHeight] = useState(392.58);
  const { learningModules } = useLearningModules();
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [bcgOpen, setBcgOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState("");

  const hasPriorities: boolean = user?.learning_module_priorities
    ? Boolean(user?.learning_module_priorities.length > 0)
    : false;
  const isLoading = profileLoading || averagesLoading;
  const onSuggestionsVisible = () => {
    setSuggestionsVisible(true);
  };

  useEffect(() => {
    if (
      router?.query &&
      router?.query?.topic &&
      router?.query?.topic !== "behaviour_change_guide_appendix" &&
      selectedModuleId === ""
    ) {
      if (router.query.topic === "behaviour_change_guide") {
        setBcgOpen(true);
      } else if (
        learningModules.some((mod) => mod.module_id === router.query.topic) &&
        router.query.topic !== "behaviour_change_guide"
      ) {
        setSelectedModuleId(router.query.topic as string);
        setOpen(true);
      }
    }
    if (selectedModuleId === "behaviour_change_guide") {
      setBcgOpen(true);
    }
  }, [router?.query, selectedModuleId, learningModules]);
  useEffect(() => {
    if (!isLoading && !user?.completed_priorities_onboarding) {
      setIsTourOpen(true);
    }
  }, [isLoading, user?.completed_priorities_onboarding]);
  return (
    <React.Fragment>
      {!purchasesLoading && !purchasedEnhance.isPurchased ? (
        <div className={classes.pageTitle}>
          <h4>
            Looks like your subscription has expired, kindly go to the home page
            and subscribe again to access content!
          </h4>
        </div>
      ) : (
        <>
          <Tour
            isTourOpen={isTourOpen}
            setIsTourOpen={setIsTourOpen}
            setTopicModalOpen={setOpen}
            setSelectedModuleId={setSelectedModuleId}
          />
          <TopicModal
            open={open}
            setOpen={setOpen}
            moduleId={selectedModuleId}
            setModuleId={setSelectedModuleId}
            isTourOpen={isTourOpen}
          />
          <BcgModal open={bcgOpen} setOpen={setBcgOpen} />
          <div>
            <section className={clsx(containerClasses.root)}>
              <div className={clsx(classes.prioritiesBackgroundRoot)}>
                <h2 className={classes.pageTitle}>
                  {"Guide to Enhancing"}
                  <IconButton
                    style={{ marginLeft: 12 }}
                    onClick={() => {
                      setIsTourOpen(true);
                    }}
                  >
                    <QuestionIcon
                      className={clsx(
                        classes.helpIcon,
                        "twentieth-step-replay"
                      )}
                    />
                  </IconButton>
                </h2>
                <Collapse timeout={0} in={!isLoading}>
                  <Fade in={!isLoading} mountOnEnter>
                    <Container maxWidth="lg">
                      <PrioritiesBanner
                        setSelectedModuleId={setSelectedModuleId}
                        setTopicModalOpen={setOpen}
                      />
                    </Container>
                  </Fade>
                </Collapse>
              </div>
            </section>
            <section style={{ minHeight: userLoading || isLoading ? 400 : 0 }}>
              <Collapse timeout={0} in={userLoading || isLoading}>
                <Fade in={userLoading || isLoading}>
                  <Container maxWidth="lg">
                    <div className={classes.loaderRoot}>
                      <CircularProgress size={60} />
                    </div>
                  </Container>
                </Fade>
              </Collapse>
              <Collapse timeout={0} in={!isLoading}>
                <Fade in={!isLoading} mountOnEnter>
                  <div>
                    <Waypoint onEnter={onSuggestionsVisible} />
                    <section className={containerClasses.root}>
                      <Container maxWidth="lg">
                        <SuggestionsSection
                          suggestionsVisible={suggestionsVisible}
                          animate={true}
                          topicModalOpen={isTourOpen}
                          setSelectedModuleId={setSelectedModuleId}
                          setTopicModalOpen={setOpen}
                          setBcgModalOpen={setBcgOpen}
                        />
                      </Container>
                    </section>
                    <Waypoint onEnter={onSuggestionsVisible} />
                    <section
                      style={{ marginBottom: 0 }}
                      className={containerClasses.root}
                    >
                      <Container maxWidth="lg">
                        <Grid spacing={4} container>
                          <Grid md={6} sm={12} xs={12} item>
                            <JournalEntriesCard height={undefined} />
                          </Grid>
                          <Grid md={6} sm={12} xs={12} item>
                            <NotYetApplicableCard
                              topicModalOpen={isTourOpen}
                              setSelectedModuleId={setSelectedModuleId}
                              setTopicModalOpen={setOpen}
                              setHeight={setCardHeight}
                            />
                          </Grid>
                        </Grid>
                      </Container>
                    </section>
                  </div>
                </Fade>
              </Collapse>
            </section>
          </div>
        </>
      )}
    </React.Fragment>
  );
};

export default PrepFqReportRoot;
